<script setup lang="ts">

import type { AcPotential } from "@/types/crm"
import type { fetchedUser } from "@/types/fetchedUser"
import type { Ref } from 'vue'
import type { ColorMetaField } from "@/types/metas"
import { ref, computed } from "vue"
import { useStore } from 'vuex'
import { useGetDocs, useGetFilteredDocs } from "@/mixins/modularFirebaseOperations"
import { useRouter } from "vue-router"
import { reactivatePotential } from "@/composables/crmBasedFunctions"
import AcDataTable from "@/components/reusable/acDataTable.vue"
import StarDisplay from "@/components/reusable/starDisplay.vue"
import modernStatusChip from "@/components/reusable/modernStatusChip.vue"
import SkeletonTable from "@/components/reusable/skeletonTable.vue"
import filterPopup from "@/components/reusable/filterPopup.vue"
import CrmPotentialCard from "@/components/crm-components/potentialCard.vue"

const { docs, isLoadingDocs } = useGetFilteredDocs("crm/potentials/list", { status: "INACTIVE" })
const potentials = docs as Ref<AcPotential[]>
const router = useRouter()

// Get job categories for filtering
const { docs: categoryDocs } = useGetDocs("crm/jobCategories/list")
const jobCategories = categoryDocs as Ref<ColorMetaField[]>

// Search and filter state
const searchQuery = ref('')
const selectedCategoryFilter = ref<ColorMetaField | null>(null)
const isShowingCategoryFilter = ref(false)
const isShowingAssignedToMe = ref(false)
const store = useStore()
const userProfile = computed(() => store.getters['user/userProfile'])
const prefView = computed(() => userProfile.value?.preferences?.crmView || 'list')

const changeUserView = () => {
    if(prefView.value === 'list'){
        store.dispatch('user/changeCrmPreference', { key: 'crmView', value: 'card' })
    } else {
        store.dispatch('user/changeCrmPreference', { key: 'crmView', value: 'list' })
    }
}

// Handle category filter selection
const handleCategorySelected = (category: ColorMetaField | undefined) => {
    selectedCategoryFilter.value = category || null
}

const handleCategoryFilterClosed = () => {
    isShowingCategoryFilter.value = false
}

const user = userProfile.value

// Filtered potentials based on search and category
const filteredPotentials = computed(() => {
    let filtered = potentials.value

    // Filter by search query
    if (searchQuery.value.trim()) {
        const query = searchQuery.value.toLowerCase().trim()
        filtered = filtered.filter(potential => 
            potential.title.toLowerCase().includes(query) ||
            potential.description?.toLowerCase().includes(query) ||
            potential.potentialCategories.some((cat: ColorMetaField) => cat.name?.toLowerCase().includes(query))
        )
    }

    // Filter by selected category
    if (selectedCategoryFilter.value) {
        filtered = filtered.filter(potential => 
            potential.potentialCategories.some((cat: ColorMetaField) => cat.id === selectedCategoryFilter.value?.id)
        )
    }

    // Filter by assigned to me
    if (isShowingAssignedToMe.value && user?.userId) {
        filtered = filtered.filter(potential => 
            potential.assignedTo?.some((assignedUser: fetchedUser) => assignedUser.id === user.userId)
        )
    }

    return filtered
})

const tableData = computed(() => {
    return filteredPotentials.value.map(doc => {
        const createdAtDate = doc.createdAt instanceof Date ? doc.createdAt : (doc.createdAt as any).toDate()
        return {
            id: doc.id, // Include the original id for navigation
            title: doc.title,
            categories: doc.potentialCategories,
            potentialRating: doc.potentialRating,
            createdAt: createdAtDate.toLocaleDateString("tr-TR"),
            actions: doc, // Include the full potential object for actions
        }
    })
})

const dataCols = [
    {
        title: "Fırsat Adı",
        key: "title",
        width: "20%",
        isSortable: true
    },
    {
        title: "Kategoriler",
        key: "categories",
        width: "20%",
        isSortable: true,
    },
    {
        title: "Oluşturulma Tarihi",
        key: "createdAt",
        width: "20%",
        isSortable: true
    },
    {
        title: "Fırsat Düzeyi",
        key: "potentialRating",
        width: "20%",
        isSortable: true,
    },
    {
        title: "İşlemler",
        key: "actions",
        width: "10%",
        isSortable: false,
    },

]

const navigateToPotential = (item: any) => {
    router.push(`/crm/potential-detail/${item.id}`)
}

const handlePotentialReactivated = async (potential: AcPotential) => {
    const confirmation = confirm("Bu potansiyel aktif hale getirilecek ve normal potansiyeller listesinde görünür olacaktır. Devam etmek istiyor musunuz?")
    console.log(confirmation)
    if(confirmation) {
        await reactivatePotential(potential)
        // Redirect to the reactivated potential's detail page
        router.push(`/crm/potential-detail/${potential.id}`)
    }
}

</script>

<template>
    <main class="main-container content-start mt-8">
        <h2 class="ac-large-title mb-0">Fırsatlar</h2>
        <div class="actions-row col-start-1 col-end-13 flex items-center justify-between gap-4">
            <!-- Search and filters on the left -->
            <div class="flex items-center gap-4">
                <!-- Search input -->
                <div class="relative">
                    <img 
                        src="../../assets/icons/search-active.png" 
                        alt="Ara" 
                        class="absolute left-3 top-1/2 transform -translate-y-1/2 w-6 h-6 opacity-50"
                    />
                    <input 
                        v-model="searchQuery"
                        type="text" 
                        placeholder="Fırsatlarda ara..."
                        class="px-12 py-3 opacity-50 text-2xl border border-gray-300 rounded-lg focus:outline-none  min-w-[250px]"
                        :class="{ '!opacity-100': searchQuery !== '' }"
                    />

                </div>
                <!-- Category filter -->
                <div class="relative">
                    <button 
                        @click.stop="isShowingCategoryFilter = true"
                        class="ac-add-button"
                        :class="{ 'stateful-background': selectedCategoryFilter }"
                    >
                        <img 
                            :class="{'filter-white': !selectedCategoryFilter}" 
                            src="../../assets/icons/filter.png" 
                            alt="Filtrele"
                        />
                        <span v-if="selectedCategoryFilter">{{ selectedCategoryFilter.name }}</span>
                        <span v-else>Kategori</span>
                    </button>
                    
                    <filter-popup 
                        @filterClosed="handleCategoryFilterClosed"
                        @item-selected="handleCategorySelected" 
                        class="absolute w-max" 
                        v-if="isShowingCategoryFilter" 
                        :itemList="jobCategories" 
                        :selectedItems="selectedCategoryFilter ? [selectedCategoryFilter] : []"
                        displayed-attribute="name" />
                </div>
                <!-- Assigned to me filter -->
                <button 
                    @click="isShowingAssignedToMe = !isShowingAssignedToMe"
                    class="ac-add-button"
                    :class="{ 'stateful-background': isShowingAssignedToMe }"
                >
                    <img 
                        :class="{'filter-white': !isShowingAssignedToMe}" 
                        src="../../assets/icons/filter.png" 
                        alt="Bana Atananları Listele"
                    />
                    <span v-if="isShowingAssignedToMe">Bana Atananlar</span>
                    <span v-else>Bana Atananları Listele</span>
                </button>
                <div @click="changeUserView()" class="ac-add-button">
                    <img class="filter-main" v-if="prefView === 'list'" src="../../assets/icons/gridView.png" alt="Filtrele">
                    <img class="filter-main" v-if="prefView === 'card'" src="../../assets/icons/listView.png" alt="Filtrele">
                    <span v-if="prefView === 'card'" >Liste Görünümü</span>
                    <span v-if="prefView === 'list'">Kart Görünümü</span>
                </div>
            </div>
            
        </div>
        <skeleton-table class="col-start-1 col-end-13 w-full" v-if="isLoadingDocs" width="100" rows="20" />
        <ac-data-table
        @row-clicked="navigateToPotential($event)"
        v-else-if="!isLoadingDocs && prefView === 'list'"
        class="w-full col-start-1 col-end-13" 
        :data="tableData" :columns="dataCols">
            <template #cell-potentialRating="{ value }">
                <star-display :picked-quantity="value" />
            </template>
            <template #cell-categories="{ value }">
                <div class="flex gap-2 overflow-x-scroll max-w-[20vw] overflow-x-bars-hidden">
                    <modern-status-chip 
                        v-for="category in value" 
                        :key="category.id"
                        :text="category.name"
                        :bgColors="{ [category.id]: category.color }"
                        :value="category.id"
                        class="text-sm"
                    />
                </div>
            </template>
            <template #cell-actions="{ value }">
                <button class="ac-table-button" @click.stop="handlePotentialReactivated(value)">
                    Aktif Hale Getir
                </button>
            </template>
        </ac-data-table>
        <div v-else-if="!isLoadingDocs && prefView === 'card'" class="col-start-1 col-end-13 grid grid-cols-4 gap-8">
            <crm-potential-card v-for="potential in filteredPotentials" :key="potential.id" :potential="potential" @click="navigateToPotential(potential)" />
        </div>
    </main>
</template>

<style lang="scss">

@import "../../styles/global/buttons.scss";

</style>